import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import NewInstructionModal from '@/components/feature/NewInstructionModal';
import InstructionAttachmentThumbnails from '@/components/feature/InstructionAttachmentThumbnails';
import { supabase } from '@/lib/supabase';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import { useManagementInstructionsList } from '@/hooks/useManagementInstructionsList';
import {
  archiveInstruction,
  deleteInstruction,
  canHardDeleteInstruction,
  emitInstructionsSync,
  type ManagementInstructionRecord,
} from '@/lib/instructionUtils';

interface ProjectRecord {
  id: string;
  project_name: string;
  project_code: string;
  status: string;
}

interface SubcontractorProfile {
  id: string;
  full_name: string | null;
  company_name: string | null;
}

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  sent:      { bg: '#F3F4F6', text: '#374151' },
  seen:      { bg: '#F3F4F6', text: '#374151' },
  in_progress: { bg: '#FEF0E7', text: '#E8621A' },
  replied:   { bg: '#FFFBEB', text: '#F59E0B' },
  completed: { bg: '#F0FDF4', text: '#059669' },
  closed:    { bg: '#FEF2F2', text: '#EF4444' },
};

const PRIORITY_STYLES: Record<string, { border: string; bar: string }> = {
  high:   { border: 'border-[#E8621A]/30', bar: 'bg-[#E8621A]' },
  medium: { border: 'border-gray-100',     bar: 'bg-[#059669]/30' },
  low:    { border: 'border-gray-100',     bar: 'bg-gray-200' },
};

function fmtDate(d: string | null): string {
  return formatDate(d);
}

function fmtTime(d: string): string {
  return formatDateTime(d);
}

export default function ManagementInstructionsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const {
    instructions,
    subcontractors,
    attachments,
    loading,
    refreshing,
    reload,
    hasData,
  } = useManagementInstructionsList({
    projectId: selectedProjectId === 'all' ? 'all' : selectedProjectId,
  });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<ManagementInstructionRecord | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Build translated status labels inside component
  const STATUS_LABELS: Record<string, string> = {
    sent: t('statusLabel.sent'),
    seen: t('statusLabel.seen'),
    in_progress: t('statusLabel.in_progress'),
    replied: t('statusLabel.replied'),
    completed: t('statusLabel.completed'),
    closed: t('statusLabel.closed'),
  };

  // Load projects
  useEffect(() => {
    supabase
      .from('jphph_projects')
      .select('id, project_name, project_code, status')
      .order('project_name', { ascending: true })
      .then(({ data }) => {
        setProjects((data || []) as ProjectRecord[]);
      });
  }, []);

  async function updateStatus(id: string, newStatus: string) {
    await supabase.from('jphph_instructions').update({ status: newStatus }).eq('id', id);
    emitInstructionsSync();
    reload();
  }

  async function handleDeleteInstruction(ins: ManagementInstructionRecord) {
    setActionError(null);
    const result = await deleteInstruction(ins);
    if (result.error) {
      setActionError(result.error);
      return;
    }
    setConfirmDeleteId(null);
    reload();
  }

  async function handleArchiveInstruction(id: string, projectId: string) {
    setActionError(null);
    const result = await archiveInstruction(id, projectId);
    if (result.error) {
      setActionError(result.error);
      return;
    }
    reload();
  }

  const showInitialLoading = loading && !hasData;

  const filtered = instructions.filter((ins) => {
    if (statusFilter !== 'all' && ins.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && ins.priority !== priorityFilter) return false;
    return true;
  });

  const grouped = filtered.reduce<Record<string, ManagementInstructionRecord[]>>((acc, ins) => {
    const pid = ins.project_id;
    if (!acc[pid]) acc[pid] = [];
    acc[pid].push(ins);
    return acc;
  }, {});

  const stats = {
    total: instructions.length,
    open: instructions.filter((i) => i.status === 'open' || i.status === 'sent' || i.status === 'in_progress').length,
    completed: instructions.filter((i) => i.status === 'completed').length,
    closed: instructions.filter((i) => i.status === 'closed').length,
  };

  const getSubName = (id: string | null) => {
    if (!id) return t('statusLabel.unassigned');
    const p = subcontractors.find((s) => s.id === id);
    return p?.full_name || p?.company_name || t('statusLabel.unknown');
  };

  const getProjectName = (id: string) => {
    const p = projects.find((proj) => proj.id === id);
    return p?.project_name || t('statusLabel.unknown_project');
  };

  const openInstructionModal = () => {
    setShowCreateModal(true);
  };

  return (
    <MgmtLayout title={t('instructions.title')}>
      <div className="px-4 lg:px-6 py-5 max-w-5xl mx-auto w-full overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="min-w-0">
            <h2 className="text-[#1A1A1A] text-lg font-bold">{t('instructions.title')}</h2>
            <p className="text-[#9CA3AF] text-sm mt-0.5">{t('instructions.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => reload()}
              className="flex items-center gap-1.5 bg-white border border-gray-200 text-[#374151] px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
              title={t('btn.reload')}
            >
              <i className="ri-refresh-line"></i>
              <span className="hidden sm:inline">{t('btn.reload')}</span>
            </button>
            <button
              onClick={openInstructionModal}
              className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              <span className="hidden sm:inline">{t('instructions.newInstruction')}</span>
              <span className="sm:hidden">{t('mobile.sendInstruction')}</span>
            </button>
          </div>
        </div>

        {refreshing && hasData && (
          <div className="flex items-center gap-2 mb-3 px-1">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-sm" />
            <span className="text-[#92400E] text-[11px] font-medium">{t('progress.refreshing', 'Refreshing...')}</span>
          </div>
        )}

        {actionError && (
          <div className="mb-3 px-3 py-2 rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[#EF4444] text-xs">
            {actionError}
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: t('instructions.total'), value: stats.total, color: '#374151', bg: '#F9FAFB' },
            { label: t('instructions.open'), value: stats.open, color: '#E8621A', bg: '#FEF0E7' },
            { label: t('instructions.completed'), value: stats.completed, color: '#059669', bg: '#F0FDF4' },
            { label: t('instructions.closed'), value: stats.closed, color: '#EF4444', bg: '#FEF2F2' },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-gray-100 p-4" style={{ backgroundColor: s.bg }}>
              <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className="text-[#9CA3AF] text-xs mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-2 mb-4">
          {/* Dropdowns row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#374151] cursor-pointer"
            >
              <option value="all">{t('instructions.allProjects')}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.project_name}</option>
              ))}
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#374151] cursor-pointer"
            >
              <option value="all">{t('instructions.allPriorities')}</option>
              <option value="high">{t('priority.high')}</option>
              <option value="medium">{t('priority.medium')}</option>
              <option value="low">{t('priority.low')}</option>
            </select>
          </div>
          {/* Status filter chips — horizontal scroll on mobile */}
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex flex-nowrap gap-1.5 min-w-max">
              {[
                { key: 'all', label: t('instructions.filterAll') },
                { key: 'sent', label: t('instructions.sent') },
                { key: 'in_progress', label: t('instructions.inProgress') },
                { key: 'completed', label: t('instructions.completed') },
                { key: 'closed', label: t('instructions.closed') },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setStatusFilter(s.key)}
                  className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors min-h-[36px] ${
                    statusFilter === s.key ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Instructions list */}
        {showInitialLoading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-file-list-3-line text-5xl"></i>
            </div>
            <p className="text-[#374151] font-semibold mb-1">{t('instructions.noInstructions')}</p>
            <p className="text-[#9CA3AF] text-sm">{t('instructions.createInstruction')}</p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={openInstructionModal}
                className="inline-flex items-center gap-2 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>{t('instructions.createFirstInstruction')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([projectId, projectInstructions]) => {
              const projectName = getProjectName(projectId);
              const project = projects.find((p) => p.id === projectId);
              return (
                <div key={projectId}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <i className="ri-building-line text-[#9CA3AF] text-sm"></i>
                    <button
                      onClick={() => navigate(`/management/projects/${projectId}?tab=Instructions`)}
                      className="text-sm font-bold text-[#1A1A1A] cursor-pointer hover:text-[#E8621A] transition-colors"
                    >
                      {projectName}
                    </button>
                    {project && (
                      <span className="text-[10px] text-[#9CA3AF]">{project.project_code}</span>
                    )}
                    <span className="text-[10px] text-[#9CA3AF] ml-auto">{projectInstructions.length} {t('instructions.items')}</span>
                  </div>

                  <div className="space-y-3">
                    {projectInstructions.map((ins) => {
                      const isExpanded = expandedId === ins.id;
                      const statusStyle = STATUS_STYLES[ins.status] ?? STATUS_STYLES.sent;
                      const priorityStyle = PRIORITY_STYLES[ins.priority] ?? PRIORITY_STYLES.medium;
                      const insAttachments = attachments[ins.id] || [];

                      return (
                        <div key={ins.id} className={`bg-white rounded-2xl border overflow-hidden ${priorityStyle.border}`}>
                          <div className={`h-1 ${priorityStyle.bar}`}></div>
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                                  <h4 className="text-sm font-semibold text-[#1A1A1A] leading-snug">{ins.instruction_title}</h4>
                                  {ins.instruction_type === 'change_order' && (
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#F5F3FF] text-[#7C3AED] whitespace-nowrap">{t('instructions.changeOrder')}</span>
                                  )}
                                </div>
                              </div>
                              <span
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap"
                                style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                              >
                                {STATUS_LABELS[ins.status] ?? ins.status}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="text-[10px] font-bold text-[#0891B2] bg-[#EFF6FF] px-2 py-0.5 rounded-full">
                                {t('instructions.to')}: {getSubName(ins.to_user_id)}
                              </span>
                              <span className="text-[10px] text-[#9CA3AF]">{fmtTime(ins.created_at)}</span>
                              {ins.due_at && (
                                <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-full">
                                  {t('instructions.due')} {fmtDate(ins.due_at)}
                                </span>
                              )}
                              {ins.priority === 'high' && (
                                <span className="text-[10px] font-bold text-[#EF4444] bg-[#FEF2F2] px-2 py-0.5 rounded-full">
                                  {t('instructions.highPriority')}
                                </span>
                              )}
                              {ins.requires_response && (
                                <span className="text-[10px] font-bold text-[#059669] bg-[#F0FDF4] px-2 py-0.5 rounded-full">
                                  {t('instructions.responseRequired')}
                                </span>
                              )}
                            </div>

                            <p className={`text-[#6B7280] text-sm leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                              {ins.body}
                            </p>
                            {ins.body.length > 120 && (
                              <button
                                onClick={() => setExpandedId(isExpanded ? null : ins.id)}
                                className="text-[#059669] text-xs font-semibold cursor-pointer mt-1.5 whitespace-nowrap"
                              >
                                {isExpanded ? t('instructions.showLess') : t('instructions.readMore')}
                              </button>
                            )}

                            {insAttachments.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-50">
                                <InstructionAttachmentThumbnails files={insAttachments} maxVisible={3} />
                              </div>
                            )}

                            {ins.management_note && (
                              <div className="mt-2 bg-[#F9FAFB] rounded-xl px-3 py-2 border border-gray-100">
                                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-0.5">{t('instructions.managementNote')}</p>
                                <p className="text-xs text-[#374151] italic">{ins.management_note}</p>
                              </div>
                            )}

                            <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => setEditingInstruction(ins)}
                                className="px-3 py-1.5 rounded-lg bg-[#EFF6FF] text-[#0891B2] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#0891B2]/10 transition-colors"
                              >
                                <i className="ri-edit-line mr-0.5"></i>Edit
                              </button>
                              {ins.status !== 'closed' && (
                                <button
                                  onClick={() => void handleArchiveInstruction(ins.id, ins.project_id)}
                                  className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#6B7280] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-gray-200 transition-colors"
                                >
                                  <i className="ri-archive-line mr-0.5"></i>Archive
                                </button>
                              )}
                              <button
                                onClick={() => setConfirmDeleteId(ins.id)}
                                className="px-3 py-1.5 rounded-lg bg-[#FEF2F2] text-[#EF4444] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#EF4444]/10 transition-colors"
                              >
                                <i className="ri-delete-bin-line mr-0.5"></i>Delete
                              </button>
                              {ins.status !== 'completed' && ins.status !== 'closed' && (
                                <button
                                  onClick={() => updateStatus(ins.id, 'completed')}
                                  className="px-3 py-1.5 rounded-lg bg-[#F0FDF4] text-[#059669] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#059669]/10 transition-colors"
                                >
                                  <i className="ri-checkbox-circle-line mr-0.5"></i>{t('instructions.markCompleted')}
                                </button>
                              )}
                              {ins.status !== 'completed' && ins.status !== 'closed' && (
                                <button
                                  onClick={() => updateStatus(ins.id, 'closed')}
                                  className="px-3 py-1.5 rounded-lg bg-[#FEF2F2] text-[#EF4444] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#EF4444]/10 transition-colors"
                                >
                                  <i className="ri-close-circle-line mr-0.5"></i>{t('instructions.markClosed')}
                                </button>
                              )}
                              <button
                                onClick={() => navigate(`/management/projects/${ins.project_id}?tab=Instructions`)}
                                className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-gray-200 transition-colors"
                              >
                                <i className="ri-external-link-line mr-0.5"></i>{t('instructions.viewInProject')}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(showCreateModal || editingInstruction) && (
        <NewInstructionModal
          show={showCreateModal || !!editingInstruction}
          onClose={() => {
            setShowCreateModal(false);
            setEditingInstruction(null);
          }}
          projectId={editingInstruction?.project_id || (selectedProjectId !== 'all' ? selectedProjectId : '')}
          allowProjectChange={!editingInstruction}
          editingInstruction={editingInstruction}
          source="sidebar-instructions"
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingInstruction(null);
            reload();
          }}
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDeleteId(null)} />
          <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full">
            <h4 className="text-[#1A1A1A] font-bold text-sm mb-2">Delete Instruction?</h4>
            <p className="text-[#6B7280] text-xs mb-4">
              {(() => {
                const ins = instructions.find((i) => i.id === confirmDeleteId);
                if (ins && canHardDeleteInstruction(ins)) {
                  return 'This internal instruction will be permanently deleted.';
                }
                return 'Sent instructions are archived instead of permanently deleted.';
              })()}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 rounded-xl bg-gray-100 text-[#374151] text-xs font-semibold cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const ins = instructions.find((i) => i.id === confirmDeleteId);
                  if (ins) void handleDeleteInstruction(ins);
                }}
                className="px-4 py-2 rounded-xl bg-[#EF4444] text-white text-xs font-semibold cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </MgmtLayout>
  );
}